import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import DashboardShell from "../components/DashboardShell";
import Modal from "../components/Modal";
import { api } from "../lib/api";

const STATUSES = ["Open", "Assigned", "Work In Progress", "Pending Customer", "Resolved", "Ticket Closed"];
const STATUS_COLORS = { "Open":"#00aaff","Assigned":"#00aaff","Work In Progress":"#f59e0b","Pending Customer":"#a855f7","Resolved":"#39ff14","Ticket Closed":"#4b5563" };

export default function AdminTickets() {
  const nav = useNavigate();
  const [status, setStatus] = useState("");
  const [q, setQ] = useState("");
  const [days, setDays] = useState(0);
  const [items, setItems] = useState([]);
  const [staff, setStaff] = useState([]);
  const [selected, setSelected] = useState(null);
  const [busy, setBusy] = useState(false);
  const [closing, setClosing] = useState(false);

  const load = async () => {
    const { data } = await api.get(`/admin/tickets`, { params: { status, q, days } });
    setItems(data.items || []);
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [status, days]);
  useEffect(() => { api.get(`/admin/tickets/staff`).then((r) => setStaff(r.data.items || [])); }, []);

  const openTicket = async (number) => {
    const { data } = await api.get(`/tickets/${number}`);
    setSelected(data);
  };

  const updateStatus = async (newStatus, assigned) => {
    if (!selected) return;
    setBusy(true);
    try {
      const { data } = await api.patch(`/admin/tickets/${selected.number}/status`, { status: newStatus, assigned_to_user_id: assigned || null });
      setSelected(data); await load();
    } finally { setBusy(false); }
  };

  const reply = async (content, is_internal) => {
    await api.post(`/tickets/${selected.number}/reply`, { content, is_internal });
    await openTicket(selected.number);
  };

  const runAutoClose = async () => {
    setClosing(true);
    try {
      const { data } = await api.post(`/admin/tickets/run-autoclose`);
      window.alert(`Auto-closed ${data.closed} ticket(s).`);
      await load();
    } finally { setClosing(false); }
  };

  return (
    <DashboardShell>
      <div className="space-y-5" data-testid="admin-tickets-page">
        <div className="flex items-center justify-between flex-wrap gap-3">
          <div>
            <div className="font-mono-tech text-[10px] uppercase tracking-[0.32em] text-[#39ff14]">// Admin · Tickets</div>
            <h1 className="text-2xl lg:text-3xl font-semibold mt-1">All Tickets</h1>
          </div>
          <button onClick={runAutoClose} disabled={closing} className="csc-btn-ghost" data-testid="run-autoclose">{closing ? "Running…" : "Run Auto-Close Now"}</button>
        </div>

        <div className="csc-card p-3 flex items-center gap-2 flex-wrap">
          <input value={q} onChange={(e) => setQ(e.target.value)} onKeyDown={(e) => e.key === "Enter" && load()} placeholder="Search title / user…" className="csc-input !py-2 flex-1 min-w-[180px]" data-testid="admin-tix-search" />
          <select value={status} onChange={(e) => setStatus(e.target.value)} className="csc-input !py-2" data-testid="admin-tix-status">
            <option value="">All statuses</option>
            {STATUSES.map((s) => <option key={s} value={s}>{s}</option>)}
          </select>
          <select value={days} onChange={(e) => setDays(parseInt(e.target.value))} className="csc-input !py-2">
            <option value={0}>All time</option>
            <option value={7}>Last 7 days</option>
            <option value={30}>Last 30 days</option>
            <option value={90}>Last 90 days</option>
          </select>
          <button onClick={load} className="csc-btn-ghost !py-2">Apply</button>
        </div>

        <div className="csc-card overflow-hidden">
          <table className="w-full text-sm">
            <thead className="font-mono-tech text-[10px] uppercase tracking-wider text-[#94a3b8] bg-[#050d1a]">
              <tr><th className="text-left px-3 py-2">#</th><th className="text-left px-3 py-2">User</th><th className="text-left px-3 py-2">Title</th><th className="text-left px-3 py-2">Status</th><th className="text-left px-3 py-2">Assigned</th><th className="text-left px-3 py-2">Created</th></tr>
            </thead>
            <tbody>
              {items.length === 0 && <tr><td colSpan={6} className="text-center text-[#94a3b8] py-6 font-mono-tech text-xs uppercase tracking-wider">No tickets</td></tr>}
              {items.map((t) => (
                <tr key={t.id} className="border-t border-[rgba(0,170,255,0.18)] hover:bg-[#00aaff]/5 cursor-pointer" onClick={() => openTicket(t.number)} data-testid={`admin-tix-${t.number}`}>
                  <td className="px-3 py-2 font-mono-tech text-white">#{t.number}</td>
                  <td className="px-3 py-2 text-[#94a3b8]">{t.user_name}<br /><span className="font-mono-tech text-[10px]">{t.user_email}</span></td>
                  <td className="px-3 py-2 text-white truncate max-w-xs">{t.title}</td>
                  <td className="px-3 py-2"><span className="font-mono-tech text-[10px] uppercase tracking-wider px-2 py-0.5 rounded" style={{ background: `${STATUS_COLORS[t.status] || "#00aaff"}26`, color: STATUS_COLORS[t.status] }}>{t.status}</span></td>
                  <td className="px-3 py-2 text-[#94a3b8]">{t.assigned_to_name || "—"}</td>
                  <td className="px-3 py-2 font-mono-tech text-[11px] text-[#94a3b8]">{new Date(t.created_at).toLocaleString()}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      <Modal open={!!selected} onClose={() => setSelected(null)} className="max-w-3xl max-h-[90vh] p-6 space-y-4" data-testid="admin-ticket-modal" ariaLabel="Ticket detail">
        {selected && (<>
          <div className="flex items-start justify-between gap-3">
            <div>
              <div className="font-mono-tech text-[10px] uppercase tracking-[0.22em] text-[#94a3b8]">#{selected.number} · {selected.custom_category ? `Other — ${selected.custom_category}` : selected.category}</div>
              <h2 className="text-xl font-semibold mt-1">{selected.title}</h2>
              <div className="font-mono-tech text-[11px] text-[#94a3b8] mt-1">{selected.user_name} · {selected.user_email}</div>
            </div>
            <button onClick={() => setSelected(null)} className="csc-btn-ghost" data-testid="admin-ticket-close">Close</button>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
            <div>
              <label className="csc-label">Status</label>
              <select value={selected.status} onChange={(e) => updateStatus(e.target.value)} className="csc-input !py-2" disabled={busy} data-testid="admin-status-select">
                {STATUSES.map((s) => <option key={s} value={s}>{s}</option>)}
              </select>
            </div>
            <div>
              <label className="csc-label">Assign to</label>
              <select value={selected.assigned_to_user_id || ""} onChange={(e) => updateStatus("Assigned", e.target.value)} className="csc-input !py-2" disabled={busy} data-testid="admin-assign-select">
                <option value="">—</option>
                {staff.map((s) => <option key={s.id} value={s.id}>{s.name} ({s.role})</option>)}
              </select>
            </div>
          </div>

          <section className="csc-card p-4">
            <h3 className="font-mono-tech text-[10px] uppercase tracking-[0.28em] text-[#94a3b8] mb-2">Description</h3>
            <p className="text-sm whitespace-pre-wrap">{selected.description}</p>
          </section>

          <section className="csc-card p-4">
            <h3 className="font-mono-tech text-[10px] uppercase tracking-[0.28em] text-[#94a3b8] mb-2">Thread</h3>
            <div className="space-y-2">
              {selected.replies.map((r) => (
                <div key={r.id} className={`p-2 rounded ${r.is_internal ? "bg-amber-500/10 border border-amber-500/30" : r.by_role === "admin" ? "bg-[#00aaff]/10 border border-[#00aaff]/30" : "bg-[#0d1b2e] border border-[rgba(0,170,255,0.18)]"}`}>
                  <div className="flex items-center justify-between text-xs">
                    <span>{r.by_name} {r.is_internal && <span className="font-mono-tech text-[9px] uppercase ml-1 text-amber-300">INTERNAL</span>}</span>
                    <span className="font-mono-tech text-[10px] text-[#94a3b8]">{new Date(r.at).toLocaleString()}</span>
                  </div>
                  <div className="text-sm mt-1 whitespace-pre-wrap">{r.content}</div>
                </div>
              ))}
            </div>
            <AdminReplyBox onSend={reply} />
          </section>
        </>)}
      </Modal>
    </DashboardShell>
  );
}

function AdminReplyBox({ onSend }) {
  const [content, setContent] = useState("");
  const [internal, setInternal] = useState(false);
  return (
    <div className="mt-3 space-y-2">
      <textarea value={content} onChange={(e) => setContent(e.target.value)} rows={3} placeholder="Reply or internal note…" className="csc-input resize-none" data-testid="admin-reply-input" />
      <div className="flex items-center justify-between">
        <label className="flex items-center gap-2 text-xs text-[#94a3b8]">
          <input type="checkbox" checked={internal} onChange={(e) => setInternal(e.target.checked)} className="accent-amber-400" data-testid="admin-internal-toggle" />
          Internal note (admin-only)
        </label>
        <button onClick={() => { if (content.trim()) { onSend(content, internal); setContent(""); } }} className="csc-btn-primary !py-2" data-testid="admin-reply-send">Send</button>
      </div>
    </div>
  );
}
